import { useState, useEffect } from 'react';
import { getActiveAgents } from '@/lib/agents-api';
import { IconByName } from '@/lib/category-icons';
import type { CoachingAgent } from '@/types';

interface AgentSelectorProps {
  onSelectAgent: (agentId: string) => void;
  selectedAgentId?: string;
}

export function AgentSelector({ onSelectAgent, selectedAgentId }: AgentSelectorProps) {
  const [agents, setAgents] = useState<CoachingAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadAgents() {
      try {
        const data = await getActiveAgents();
        setAgents(data);
      } catch (error) {
        console.error('Failed to load agents:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadAgents();
  }, []);

  if (isLoading) {
    return <div className="text-center py-8 text-slate-400">Loading coaches...</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {agents.map((agent) => (
        <button
          key={agent.id}
          onClick={() => onSelectAgent(agent.id)}
          className={`
            group relative p-6 rounded-xl border-2 transition-all
            ${
              selectedAgentId === agent.id
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-slate-700 bg-slate-800/50 hover:border-amber-500/50'
            }
          `}
        >
          <div className="flex items-start gap-4">
            <div
              className={`
                p-3 rounded-lg transition-colors
                ${
                  selectedAgentId === agent.id
                    ? 'bg-amber-500 text-slate-900'
                    : 'bg-slate-700 text-amber-500 group-hover:bg-amber-500/20'
                }
              `}
            >
              <IconByName name={agent.iconName} className="h-6 w-6" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-semibold text-slate-100 mb-1">{agent.name}</h3>
              {agent.description && (
                <p className="text-sm text-slate-400">{agent.description}</p>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
